import React from 'react';
import { Box, Button, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ResponsiveTable, { ResponsiveTableColumn } from '../common/ResponsiveTable';
import { LINE_STATUS } from '../../types/pettyCash';
import type { AllocationType, ClaimAttachment, ClaimLine, ClaimLineInput, LineCheck, PettyCashConfig } from '../../types/pettyCash';
import { accountForCategory, formatAmount, formatDate, lineTotals } from '../../utils/pettyCash';
import AccountSourceChip from './AccountSourceChip';

/** A line on screen: what was typed, plus what the server knows once it is saved. */
export type ScreenLine = ClaimLineInput & Partial<Omit<ClaimLine, keyof ClaimLineInput>> & { pendingFile?: File | null; localChecks?: LineCheck[] };

interface Props {
  lines: ScreenLine[];
  config: PettyCashConfig;
  currency: string;
  allocationType: AllocationType;
  editable: boolean;
  /**
   * Finance may change the lines (account, VAT code, dimensions, type, disallow). The server's line.canCorrect
   * decides per line when it is given (after a posting failure only the receipts not yet in SAP).
   */
  finance: boolean;
  /** The claim is partly posted: the change button reads "Correct" (a receipt SAP refused). */
  correcting?: boolean;
  /** The claim reached SAP: show each line's posting status and SAP numbers. */
  showPosting: boolean;
  onEdit?: (index: number) => void;
  onDelete?: (index: number) => void;
  onFinance?: (line: ScreenLine) => void;
  onOpenAttachment: (a: ClaimAttachment) => void;
}

const LinesTable: React.FC<Props> = ({
  lines, config, currency, allocationType, editable, finance, correcting, showPosting, onEdit, onDelete, onFinance, onOpenAttachment,
}) => {
  const { settings, categories } = config;
  const canChange = (l: ScreenLine): boolean => !!l.lineId && (l.canCorrect ?? finance);
  const totals = lineTotals(lines.map((l) => ({ amount: l.amount, vatAmount: l.vatAmount, total: l.total, status: l.status })));

  const columns: ResponsiveTableColumn<ScreenLine>[] = [
    { id: 'n', header: '#', cell: (l) => l.lineNum, width: 36 },
    {
      id: 'type',
      header: 'Type',
      cell: (l) => <Chip size="small" label={l.lineType === 'VAT' ? 'VAT' : 'Non-VAT'} color={l.lineType === 'VAT' ? 'info' : 'default'} variant={l.lineType === 'VAT' ? 'filled' : 'outlined'} />,
      width: 84,
    },
    {
      id: 'supplier',
      header: 'Supplier',
      cell: (l) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>{l.supplierName || '—'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {l.lineType === 'VAT'
              ? [l.cardCode, l.supplierVat ? `VAT ${l.supplierVat}` : ''].filter(Boolean).join(' · ')
              : `posts to ${settings.cashSupplier ? `${settings.cashSupplier} ${settings.cashSupplierName || ''}`.trim() : 'the cash supplier'}`}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'invoice',
      header: 'Invoice no. and date',
      cell: (l) => (
        <Box>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{l.invoiceNo || '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{formatDate(l.invoiceDate)}</Typography>
        </Box>
      ),
    },
    { id: 'amount', header: 'Before VAT', cell: (l) => formatAmount(l.amount), align: 'right' },
    {
      id: 'vat',
      header: 'VAT',
      align: 'right',
      cell: (l) => (
        <Box>
          <Typography variant="body2">{formatAmount(l.vatAmount)}</Typography>
          <Typography variant="caption" color="text.secondary">{l.vatCode || ''}{l.lineType === 'VAT' && l.vatRate ? ` · ${l.vatRate}%` : ''}</Typography>
        </Box>
      ),
    },
    { id: 'total', header: 'Total', cell: (l) => <Typography variant="body2" fontWeight={600}>{formatAmount(l.total)}</Typography>, align: 'right' },
    {
      id: 'category',
      header: 'Expense category and G/L',
      hideOnMobile: false,
      cell: (l) => {
        const category = categories.find((c) => c.categoryId === l.categoryId) || null;
        // Saved lines carry the server's account and where it comes from; a new line shows what its category says.
        if (l.effectiveAccount !== undefined) {
          return (
            <Box>
              <Typography variant="body2">{l.categoryName || category?.name || '—'}</Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {l.effectiveAccount ? `${l.effectiveAccount}${l.effectiveAccountName ? ` ${l.effectiveAccountName}` : ''}` : 'no account yet'}
                {l.dims?.['5'] ? ` · ${l.dims['5']}` : ''}
              </Typography>
              <AccountSourceChip source={l.accountSource ?? (l.glAccount ? 'line' : null)} rule={l.accountRule ?? null} />
            </Box>
          );
        }
        const predicted = accountForCategory(category, allocationType);
        return (
          <Box>
            <Typography variant="body2">{l.categoryName || category?.name || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {predicted.ruleBasis ? `SAP's G/L rules (${predicted.ruleBasis})` : predicted.code ? `${predicted.code}${predicted.name ? ` ${predicted.name}` : ''}` : 'no account yet'}
              {l.auxiliary || predicted.auxiliary ? ` · ${l.auxiliary || predicted.auxiliary}` : ''}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'receipt',
      header: 'Receipt',
      cell: (l) => {
        const files = l.attachments || [];
        if (!files.length && !l.pendingFile) return <Typography variant="caption" color="text.secondary">none</Typography>;
        return (
          <Stack spacing={0.25}>
            {files.map((a) => (
              <Link key={a.attachmentId} component="button" type="button" variant="body2" onClick={() => onOpenAttachment(a)} sx={{ textAlign: 'left' }}>
                {a.fileName}
              </Link>
            ))}
            {l.pendingFile ? <Typography variant="caption" color="text.secondary">{l.pendingFile.name} (not uploaded yet)</Typography> : null}
          </Stack>
        );
      },
    },
    {
      id: 'check',
      header: showPosting ? 'Status' : 'Check',
      cell: (l) => {
        if (showPosting && l.status) {
          const s = LINE_STATUS[l.status];
          return (
            <Stack spacing={0.5} alignItems="flex-start">
              <Chip size="small" label={s.label} color={s.color} />
              {l.sapInvoiceNum ? <Typography variant="caption">A/P invoice {l.sapInvoiceNum}</Typography> : null}
              {l.sapPaymentNum ? <Typography variant="caption">{l.sapPaymentKind === 'Journal' ? 'Journal entry' : 'Payment'} {l.sapPaymentNum}</Typography> : null}
              {l.status === 'Disallowed' && l.disallowReason ? <Typography variant="caption" color="text.secondary">{l.disallowReason}</Typography> : null}
              {l.postingError ? <Typography variant="caption" color="error">{l.postingError}</Typography> : null}
            </Stack>
          );
        }
        if (l.status === 'Disallowed') {
          return (
            <Tooltip title={l.disallowReason || ''}>
              <Chip size="small" color="warning" label="Disallowed" />
            </Tooltip>
          );
        }
        const checks = [...(l.checks || []), ...(l.localChecks || [])];
        const errors = checks.filter((c) => c.level === 'error');
        const warnings = checks.filter((c) => c.level === 'warning');
        if (!errors.length && !warnings.length) return <Chip size="small" color="success" label="OK" variant="outlined" />;
        return (
          <Stack spacing={0.5} alignItems="flex-start">
            {/* One code can come more than once (two duplicate claims, two open POs). */}
            {errors.map((c, i) => <Typography key={`e-${c.code}-${i}`} variant="caption" color="error">{c.message}</Typography>)}
            {warnings.map((c, i) => <Typography key={`w-${c.code}-${i}`} variant="caption" sx={{ color: '#7a4b00' }}>{c.message}</Typography>)}
          </Stack>
        );
      },
    },
  ];

  if (editable || (onFinance && lines.some(canChange))) {
    columns.push({
      id: 'actions',
      header: '',
      align: 'right',
      cell: (l, i) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {editable && onEdit ? <IconButton size="small" aria-label={`Edit receipt ${l.lineNum}`} onClick={() => onEdit(i)}><EditOutlinedIcon fontSize="small" /></IconButton> : null}
          {editable && onDelete ? <IconButton size="small" aria-label={`Remove receipt ${l.lineNum}`} onClick={() => onDelete(i)}><DeleteOutlineIcon fontSize="small" /></IconButton> : null}
          {onFinance && canChange(l) ? (
            <Button size="small" variant={correcting && l.status === 'Failed' ? 'contained' : 'outlined'} color={correcting && l.status === 'Failed' ? 'error' : 'primary'} onClick={() => onFinance(l)}>
              {correcting ? 'Correct' : 'Change'}
            </Button>
          ) : null}
        </Stack>
      ),
    });
  }

  return (
    <Box>
      <ResponsiveTable
        ariaLabel="Receipts"
        columns={columns}
        rows={lines}
        getRowKey={(l, i) => l.lineId ?? `new-${i}`}
        emptyMessage="No receipts yet"
      />
      {lines.length ? (
        <Stack direction="row" spacing={3} justifyContent="flex-end" sx={{ px: 1.5, py: 1, borderTop: '1px solid #e3e8eb' }} flexWrap="wrap" useFlexGap>
          <Typography variant="body2">Before VAT <strong>{formatAmount(totals.beforeVat)}</strong></Typography>
          <Typography variant="body2">VAT <strong>{formatAmount(totals.vat)}</strong></Typography>
          <Typography variant="body2">Total <strong>{formatAmount(totals.total)} {currency}</strong></Typography>
        </Stack>
      ) : null}
    </Box>
  );
};

export default LinesTable;
